import React, { useState, useEffect, useCallback } from "react";
import { formAPI } from "../../../../../services/api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import "./10Dashboard.css";

/*
  VIP10Dashboard (단순 버전)
  ------------------------------------
  1. projectId 를 받아 formAPI.getSubjectList 로 전체 row 데이터를 가져온다.
  2. 로드된 rows 를 JSON 문자열로 그대로 화면에 표시한다.
  3. 다시 불러오기 버튼으로 새로 고침 가능.
*/

const VIP10Dashboard = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadRows = useCallback(async () => {
    if (!projectId) {
      setError("projectId 가 없습니다.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await formAPI.getVariableDataLambda(
        projectId,
        necessaryVars
      );
      setRows(list);
    } catch (err) {
      console.error(err);
      setError(err.message || "데이터 로드 실패");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  if (loading) return <div style={{ padding: "24px" }}>로딩 중...</div>;
  if (error)
    return <div style={{ padding: "24px", color: "red" }}>오류: {error}</div>;

  // Necessary Vars (해당 페이지 구현을 위해 필요한 변수)
  const necessaryVars = [
    "sex",
    "witness",
    "arrest_location",
    "immediate_CPR",
    "Mech_CPR",
    "pt_number",
  ];

  // Pie Chart Vars (각 변수의 raw 값을 리스트로 보여줌)
  const pieVars = [
    "sex",
    "witness",
    "arrest_location",
    "immediate_CPR",
    "Mech_CPR",
  ];

  //  Bar Chart Vars (각 변수의 값을 막대 그래프로 보여줌)
  const barVars = [];

  // 변수별 값 집계 → datas 객체
  const datas = necessaryVars.reduce((acc, v) => {
    acc[v] = rows.map((r) => r[v] ?? r.form_data?.[v] ?? "(blank)");
    return acc;
  }, {});

  // 색상 팔레트 (CSS 변수 활용 → fallback 색상 지정)
  const getColor = (idx) =>
    getComputedStyle(document.documentElement).getPropertyValue(
      `--dashboard-color-${idx % 7}`
    ) || ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"][idx % 5];

  // 변수별 빈도 데이터 생성
  const freqMap = (values) => {
    const m = {};
    values.forEach((v) => {
      const key = String(v);
      m[key] = (m[key] || 0) + 1;
    });
    return Object.entries(m).map(([label, value]) => ({ label, value }));
  };

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ marginTop: 0 }}>데이터 대시보드</h2>

      {/* 차트&리스트 */}

      <div
        style={{
          display: "grid",
          gap: 24,
          gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
        }}
      >
        {[...pieVars, ...barVars].map((v, idx) => {
          const chartData = freqMap(datas[v] || []);
          if (!chartData.length) return null;
          const isPie = pieVars.includes(v);
          return (
            <div key={v} className="dashboard-chart-card">
              <h4 style={{ textTransform: "capitalize", marginBottom: 8 }}>
                {v}
              </h4>
              <ResponsiveContainer width="100%" height={380}>
                {isPie ? (
                  <PieChart>
                    <Tooltip formatter={(value) => [`${value} 개`, "count"]} />
                    <Legend
                      layout="horizontal"
                      verticalAlign="bottom"
                      align="center"
                    />
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {chartData.map((_, i) => (
                        <Cell key={i} fill={getColor(i)} />
                      ))}
                    </Pie>
                  </PieChart>
                ) : (
                  <BarChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 10, bottom: 30 }}
                  >
                    <XAxis
                      dataKey="label"
                      interval={0}
                      angle={-15}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip formatter={(value) => [`${value} 개`, "count"]} />
                    <Legend
                      layout="horizontal"
                      verticalAlign="bottom"
                      align="center"
                    />
                    <Bar dataKey="value" name={v} isAnimationActive={false}>
                      {chartData.map((_, i) => (
                        <Cell key={i} fill={getColor(i)} />
                      ))}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          );
        })}

        {/* datas debug hidden (optional) */}
        {/* <details style={{ gridColumn: "1 / -1" }}>
          <summary>datas 객체 보기</summary>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
            {JSON.stringify(datas, null, 2)}
          </pre>
        </details> */}
      </div>

      {/* END 차트&리스트 */}
    </div>
  );
};

export default VIP10Dashboard;
